import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, Download, RefreshCw } from 'lucide-react';
import { Modal } from '../components/Modal';
import EditableCell from '../components/EditableCell';
import StatusBadge from '../components/StatusBadge';
import Button from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import SearchInput from '../components/ui/SearchInput';
import SegmentedControl from '../components/ui/SegmentedControl';
import { useLatestRequest } from '../hooks/useLatestRequest';
import { formatCurrency } from '../utils/priceUtils';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import { usePermission } from '../hooks/usePermission';
import LoadingState from '../components/LoadingState';
import {
  AccountsPayableListResponse,
  AccountsPayableRecord,
  AccountsPayableStatus,
  AccountsPayablePaymentRecord,
  importAccountsPayablePayments,
  getAccountsPayableById,
  listAccountsPayablePayments,
  listAccountsPayables,
  patchAccountsPayable,
  syncAccountsPayables,
  syncDuplicatesNote,
} from '../lib/accountsPayableApi';
import { createPaymentVoucher, billActionRefusalMessage } from '../lib/accountsPayablePaymentsApi';
import { fetchClientInvoices, fetchClientInvoiceSummary, patchClientInvoice, type ClientInvoiceDto, type ClientInvoiceSummary } from '../lib/clientInvoiceApi';
import PaymentCreditOption, { EXCESS_THRESHOLD } from '../features/accountsPayablePayments/PaymentCreditOption';
import { VendorSelect } from '../components/VendorSelect';
import { formatDate as fmtDate, storedDay } from "../utils/dateFormat";
import { creditDaysOnFile, invoiceDateChecks } from "../utils/paymentTerms";
import { dueDateBasisCaption, daysUsedForDueDate } from "../utils/dueDateBasis";
import { downloadObjectsCsv } from '../utils/downloadCsv';
import PayableFilterBar from '../features/accountsPayableReports/PayableFilterBar';
import { usePayableFilters } from '../features/accountsPayableReports/usePayableFilters';
import { payableFiltersToParams } from '../features/accountsPayableReports/payableFilters';
// A bill's money: TT$ / US$ from the label owner, never the locale's bare "$" (lane 5, V3d). The summary
// cards print one line per currency through the shared CurrencyLines (the server's converted single total
// is not shown: it folded USD into TTD at a default rate and nothing on the card said so, t09).
import { formatBillMoney as formatMoney } from '../features/accountsPayableReports/format';
import { CurrencyLines } from '../features/accountsPayableReports/CurrencyLines';
import { apErrorMessage } from '../features/accountsPayableReports/apErrorMessage';
import { browserLatestPaymentDay, browserToday } from '../features/vendorInvoices/paymentOnReceiptForm';
import {
  billStatusLabel,
  exceptionStatusLabel,
  isExceptionStatus,
  simpleBillStatusLabel,
} from '../features/accountsPayableReports/billLabels';
// The one owner of the payment-method vocabulary (also used by the payment run editor):
// no second hardcoded list of methods here.
import { PAYMENT_METHODS } from '../utils/paymentOnReceipt';
import DateInput from "../components/DateInput";
import { MarginByVesselPanel } from '../features/marginByVessel/MarginByVesselPanel';

type AccountingStatus = 'not_sent' | 'accounting_team';

/** Rows per page of the payables list. Named so the request and the pager agree. */
const AP_PAGE_SIZE = 25;

const ACCOUNTING_STATUS_OPTIONS: Array<{ value: AccountingStatus; label: string }> = [
  { value: 'not_sent', label: 'Not yet sent' },
  { value: 'accounting_team', label: 'Sent to accountant' },
];

// Everyday filter tabs (2026-09-22, at the owner's word): Unpaid / Partially Paid / Paid, the same
// three words the list itself reads (billLabels#simpleBillStatusLabel), plus Overdue -- a useful
// FILTER, not a fourth status word (it is still an Unpaid or Partially Paid bill, shown in red).
// On Hold and Disputed are exceptions: still real, still stop a payment run from picking a bill up,
// but a person changes them from the bill's own page now, not from a dropdown on every row, so they
// are not offered as a tab here either. Filtering to them is still possible from a report link
// (`held=only` in the URL), same as before.
// A required/optional hint painted by CSS `::after`, never a real DOM node: an `aria-hidden` span
// was tried first, but dom-testing-library's implicit label match reads a label's WHOLE textContent
// regardless of aria-hidden, so `getByLabelText('Amount')` stopped matching "Amount *". CSS content
// leaves the DOM, and every accessible name, exactly "Amount".
const REQUIRED_LABEL = "after:ml-1 after:text-rose-600 after:content-['*']";
const OPTIONAL_LABEL = "after:ml-1 after:font-normal after:text-slate-400 after:content-['(optional)']";

const STATUS_OPTIONS: Array<{ value: AccountsPayableStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'partially_paid', label: 'Partially Paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
];

function toDateInputValue(value?: string | null) {
  return storedDay(value) ?? '';
}

// An invoice date and a due date are CALENDAR DAYS stored as that day's UTC midnight, so they are
// printed by their UTC day (the same reading the editable cell below uses). Handed to `fmtDate`
// as the raw ISO instant they were read in the browser's own zone, and in Trinidad (UTC-4) the
// 21st printed as the 20th: in the CSV export, and now beside every invoice number.
function formatDate(value?: string | null) {
  return fmtDate(toDateInputValue(value), '--');
}

// The badge for the SIMPLE status word (billLabels#simpleBillStatusLabel): read off the money, plus
// red when the server says the bill is overdue -- the same red the row's own tint already used.
function simpleStatusBadgeClass(row: AccountsPayableRecord) {
  const overdue = String(row.status || '').toLowerCase() === 'overdue';
  if (overdue) return 'bg-rose-50 text-rose-700 border-rose-200';
  const label = simpleBillStatusLabel(row);
  if (label === 'Paid') return 'bg-cyan-50 text-cyan-700 border-cyan-200';
  if (label === 'Partially Paid') return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-sky-50 text-sky-700 border-sky-200';
}

// A flag for a person who has typed a supplier invoice number, not a status of its own: whether
// this row has been sent to the accounting team yet. Styled plainly (not a coloured pill) so it does
// not read as a second, competing "status" beside the Status column (2026-09-22 QA feedback).
function accountingStatusLabel(status: string | undefined | null) {
  if (String(status || '').toLowerCase() === 'accounting_team') return 'Sent to accountant';
  return 'Not yet sent';
}

function accountingStatusClass(status: string | undefined | null) {
  if (String(status || '').toLowerCase() === 'accounting_team') {
    return 'border-slate-300 bg-white text-slate-700';
  }
  return 'border-slate-200 bg-slate-50 text-slate-500';
}

// A stored 0 is the default nobody changed, not "Immediate" (NEW-2).
function vendorTermsValue(days?: number | null) {
  const value = creditDaysOnFile(days);
  return value === null ? 'Not on file' : `Net ${value}`;
}

// D109.7: the supplier's on-file terms are labelled as such wherever they are drawn, so they never
// read as the basis of the due date (that is `dueDateBasisCaption`, from the days actually used).
function vendorTermsLabel(days?: number | null) {
  const value = creditDaysOnFile(days);
  return value === null ? 'Supplier terms not on file' : `Supplier terms Net ${value}`;
}

function vendorTermsClass(days?: number | null) {
  const value = creditDaysOnFile(days);
  if (value === null) return 'bg-slate-50 text-slate-600 border-slate-200';
  if (value <= 15) return 'bg-lime-50 text-lime-700 border-lime-200';
  if (value <= 30) return 'bg-amber-50 text-amber-700 border-amber-200';
  if (value <= 45) return 'bg-orange-50 text-orange-700 border-orange-200';
  return 'bg-rose-50 text-rose-700 border-rose-200';
}

// A row is red when the SERVER says it is overdue. The list is read through `effectiveStatus`
// (BUSINESS_RULES#payable-overdue: late from the day AFTER the due date, in Trinidad time), so the
// word on the row, the Overdue filter and the Overdue card are one answer. This used to compare the
// due date with the browser's `Date.now()`, a second copy of the rule that turned a bill due today
// red from 20:00 Trinidad time the evening before.
function rowToneClass(row: AccountsPayableRecord) {
  const status = String(row.status || '').toLowerCase();
  if (status === 'paid') return 'bg-cyan-50/30';
  if (status === 'partially_paid') return 'bg-amber-50/30';
  if (status === 'on_hold') return 'bg-slate-50';
  if (status === 'disputed') return 'bg-orange-50/40';
  if (status === 'overdue') return 'bg-rose-50/40';
  return '';
}


const INFO = (
  <div className="space-y-2">
    <p>
      Accounts Payable tracks the existing Vendor Invoices records and classifies them as unpaid,
      partially paid, paid, overdue, on hold, or disputed.
    </p>
    <p>
      This screen is the registry layer for payable tracking. Payment allocation and supplier
      statements will be added in the next batch.
    </p>
  </div>
);

// ClickUp 86d42zz72: the sub-tab (Accounts Payable vs Client Invoices) lived only
// in React state, so a refresh always landed back on Accounts Payable regardless
// of which one the user had open. Read once at mount, lazily, so the very first
// render already reflects it instead of flashing the default tab first.
type AccountsTab = 'payable' | 'receivable' | 'margin';

function accountsTabFromSearch(search: string): AccountsTab {
  const tab = new URLSearchParams(search).get('tab');
  return tab === 'receivable' || tab === 'margin' ? tab : 'payable';
}

function initialAccountsTabFromUrl(): AccountsTab {
  return accountsTabFromSearch(window.location.search);
}

export const AccountsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Recording a payment needs the Financials create right on the server; without it the window
  // could be filled in and could never save, so it is not offered (as on the payment runs page).
  const { canCreate: canRecordPayments } = usePermission('financials');

  // ── Tab state ──────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<AccountsTab>(initialAccountsTabFromUrl);

  const handleTabChange = (id: AccountsTab) => {
    setActiveTab(id);
    navigate(`/accounts?tab=${id}`, { replace: true });
  };

  // Keeps the tab in sync with browser back/forward, not just the initial load.
  useEffect(() => {
    const next = accountsTabFromSearch(location.search);
    setActiveTab((prev) => (prev === next ? prev : next));
  }, [location.search]);

  // ── Client Invoices (Receivable) state ─────────────────────────
  const [clientInvoices, setClientInvoices] = useState<ClientInvoiceDto[]>([]);
  const [clientInvoicesLoading, setClientInvoicesLoading] = useState(false);
  const [clientInvoicesSearch, setClientInvoicesSearch] = useState('');
  const [clientInvoicesTotal, setClientInvoicesTotal] = useState(0);

  useEffect(() => {
    if (activeTab !== 'receivable') return;
    setClientInvoicesLoading(true);
    fetchClientInvoices(1, 100, clientInvoicesSearch, 'all')
      .then((res) => {
        setClientInvoices(res.clientInvoices || []);
        setClientInvoicesTotal(res.pagination?.total || 0);
      })
      .catch(() => setClientInvoices([]))
      .finally(() => setClientInvoicesLoading(false));
  }, [activeTab, clientInvoicesSearch]);

  // The cards cover the whole searched book (server-side), not the 100 rows below.
  const [clientInvoiceSummary, setClientInvoiceSummary] = useState<ClientInvoiceSummary | null>(null);
  const [clientInvoiceSummaryFailed, setClientInvoiceSummaryFailed] = useState(false);
  const beginSummaryRequest = useLatestRequest();
  useEffect(() => {
    if (activeTab !== 'receivable') return;
    // Last search wins: a slow answer for an older search must not replace the newer totals.
    const isCurrent = beginSummaryRequest();
    fetchClientInvoiceSummary(clientInvoicesSearch)
      .then((res) => {
        if (!isCurrent()) return;
        setClientInvoiceSummary(res);
        setClientInvoiceSummaryFailed(false);
      })
      .catch(() => {
        if (!isCurrent()) return;
        setClientInvoiceSummary(null);
        setClientInvoiceSummaryFailed(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, clientInvoicesSearch]);

  // ── AP state ───────────────────────────────────────────────────
  const [items, setItems] = useState<AccountsPayableRecord[]>([]);
  const [summary, setSummary] = useState<AccountsPayableListResponse['summary']>({
    invoiceCount: 0,
    totalAmount: 0,
    paidAmount: 0,
    outstandingAmount: 0,
    unpaidCount: 0,
    partialCount: 0,
    paidCount: 0,
    overdueCount: 0,
  });
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // What the last Sync has to say beyond "done" (D116.3: confirmed invoices it gave no bill).
  const [syncNote, setSyncNote] = useState<string | null>(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  // D86: the window's supplier area. `paymentBillFilter` is the free-text search that finds bills;
  // `paymentForm.supplier*` is the vendor picked for an ADVANCE only (the picker shows only while no
  // bill is ticked). Ticked bills pay under their own stored supplier name; nothing is typed.
  const [paymentBillFilter, setPaymentBillFilter] = useState('');
  // 'quick' is the everyday path (2026-09-22, at the owner's word): pay the ONE bill a person
  // clicked Pay on, five fields, no allocation table. 'full' is the multi-bill-per-supplier path
  // (one cheque covering several invoices, or an advance) -- still there, reached from the top
  // "Record payment" button or "+ Pay another bill" inside a quick payment.
  const [paymentMode, setPaymentMode] = useState<'quick' | 'full'>('quick');
  // The one bill a quick payment is for. Only set in 'quick' mode.
  const [quickBill, setQuickBill] = useState<AccountsPayableRecord | null>(null);
  // Why the last Save/Pay was refused, shown INSIDE the payment window (both modes). A browser
  // alert() covered the very row it was about and told the person nothing they could not lose
  // by clicking OK (QA 2026-10-08: a ticked bill at 0.00 was refused with a popup).
  const [paymentError, setPaymentError] = useState('');
  // D109.6: false while the payment-date box holds a refused date (it keeps the OLD date in the form).
  const [paymentDateValid, setPaymentDateValid] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  // t10 — Save Payment had no in-flight guard, unlike every other mutating
  // button on this page (e.g. paymentImportLoading below): a double-click or a
  // slow request left open recorded the same payment twice.
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  // Set when the supplier's invoices/payments could not be loaded. While it is
  // set the rows are EMPTY, so nothing from a previous supplier can be
  // allocated under this one's name.
  const [paymentLoadError, setPaymentLoadError] = useState<string | null>(null);
  // Whose payment-modal answer is allowed to reach the rows. TECH_DEBT #46.
  const beginPaymentModalRequest = useLatestRequest();
  const [paymentRecords, setPaymentRecords] = useState<AccountsPayablePaymentRecord[]>([]);
  const [paymentSummary, setPaymentSummary] = useState({
    paymentCount: 0,
    totalAmount: 0,
    allocatedAmount: 0,
    unallocatedAmount: 0,
  });
  const [paymentImportOpen, setPaymentImportOpen] = useState(false);
  const [paymentImportLoading, setPaymentImportLoading] = useState(false);
  const [paymentImportFile, setPaymentImportFile] = useState<File | null>(null);
  const [paymentImportSummary, setPaymentImportSummary] = useState<{ createdCount: number; errors: string[] } | null>(null);
  const [paymentRows, setPaymentRows] = useState<AccountsPayableRecord[]>([]);
  // Hold an excess over the ticked bills (or an advance) as a supplier credit. Never on by default:
  // a mistyped amount must not quietly become credit with a supplier (D49 phase 5).
  const [holdAsCredit, setHoldAsCredit] = useState(false);
  // The bill "Record a payment" was pressed on (?pay=<billId>): ticked when the window loads.
  const preTickBillId = useRef<string | null>(null);
  const lastLoadedFilter = useRef<string | null>(null);
  const [paymentForm, setPaymentForm] = useState({
    supplierName: '',
    // D86: the vendor the supplier was picked as. A name with no id is a leftover (a filter text, an old
    // bill's name) and must be picked, or matched exactly below, before a payment is recorded.
    supplierVendorId: null as string | null,
    reference: '',
    chequeNumber: '',
    onlineReferenceNumber: '',
    paymentDate: browserToday(),
    amount: '',
    currency: 'TTD',
    method: 'Cheque',
    note: '',
  });
  const [allocations, setAllocations] = useState<Record<string, { checked: boolean; amount: string }>>({});
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  // D49 phase 2: every list filter (status, supplier, search, and the due / invoice date, amount,
  // currency, due-chip and sort filters) lives in one place and STARTS from the URL, so a report
  // figure can link here (features/accountsPayableReports/payableFilters.ts). The server applies
  // all of them, and the summary cards above the list follow the same filter.
  const { filters, update: updateFilters, clear: clearFilters } = usePayableFilters();
  const { status, supplier, q: search } = filters;
  const changeFilters = (patch: Partial<typeof filters>) => {
    setPage(1);
    updateFilters(patch);
  };
  // Said in plain words when the list cannot be loaded (never the server's prose, never a code).
  const [loadError, setLoadError] = useState<string | null>(null);
  const beginListRequest = useLatestRequest();
  const bootstrapped = useRef(false);

  const load = async () => {
    // Last one wins: filters change quickly, and a slow answer to an earlier filter must not
    // replace the list for the current one (TECH_DEBT #46).
    const isCurrent = beginListRequest();
    setLoading(true);
    try {
      const res = await listAccountsPayables({
        page,
        limit: AP_PAGE_SIZE,
        ...payableFiltersToParams(filters),
      });
      if (!isCurrent()) return;
      setLoadError(null);
      const data = res.data;
      setItems(data.items || []);
      setSummary(
        data.summary || {
          invoiceCount: 0,
          totalAmount: 0,
          paidAmount: 0,
          outstandingAmount: 0,
          unpaidCount: 0,
          partialCount: 0,
          paidCount: 0,
          overdueCount: 0,
        }
      );
      setPages(Math.max(1, Number(data.pages || 1)));
    } catch (err) {
      if (!isCurrent()) return;
      setItems([]);
      setLoadError(apErrorMessage(err));
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const syncAndLoad = async (force = false) => {
    setSyncing(true);
    setSyncNote(null);
    try {
      const res = await syncAccountsPayables(force);
      // D116.3: invoices the sync left without a bill because a twin's bill already stands.
      setSyncNote(syncDuplicatesNote(res?.data));
      await load();
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    void (async () => {
      try {
        await load();
      } finally {
        bootstrapped.current = true;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!bootstrapped.current) return;
    const t = window.setTimeout(() => {
      void load();
    }, 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  async function saveField(id: string, payload: Record<string, unknown>) {
    try {
      await patchAccountsPayable(id, payload);
    } catch (e: any) {
      // Said in words chosen by the CODE (a refused Mark Pending lists its vouchers on the bill's own
      // page); the list is reloaded so the control shows what is really stored.
      alert(e?.code ? billActionRefusalMessage(e) : e?.message || 'That could not be saved. Nothing has changed.');
    }
    await load();
  }

  const exportRows = useMemo(
    () =>
      items.map((row) => ({
        'Invoice #': row.invoiceNumber || '',
        Supplier: row.supplierName || '',
        Contact: row.supplierContact || '',
        'Invoice Date': formatDate(row.invoiceDate),
        'Due Date': formatDate(row.dueDate),
        Currency: row.currency || 'TTD',
        'Total Amount': row.totalAmount ?? 0,
        'Paid Amount': row.paidAmount ?? 0,
        'Outstanding Amount': row.outstandingAmount ?? 0,
        Status: billStatusLabel(row.status),
        'Accounting Status': accountingStatusLabel(row.accountingStatus),
        'Supplier terms': vendorTermsValue(row.vendorCreditDays),
        'Days used': daysUsedForDueDate(row.invoiceDate, row.dueDate) ?? '',
        Notes: row.notes || '',
      })),
    [items]
  );

  const tickedRows = useMemo(() => paymentRows.filter((row) => allocations[row._id]?.checked), [allocations, paymentRows]);
  // What a payment for the ticked bills is sent under: the first ticked bill's stored name.
  const tickedSupplierName = (tickedRows[0]?.supplierName || '').trim();
  const hasTickedBills = tickedRows.length > 0;

  // A refusal describes the form as it was when Save was clicked: any edit makes it stale.
  useEffect(() => {
    setPaymentError('');
  }, [allocations, paymentForm, holdAsCredit]);

  // A bill being ticked drops any picked vendor, so a stale pick can never ride along.
  useEffect(() => {
    if (hasTickedBills) {
      setPaymentForm((prev) => (prev.supplierVendorId || prev.supplierName ? { ...prev, supplierName: '', supplierVendorId: null } : prev));
    }
  }, [hasTickedBills]);

  const selectedAllocationTotal = useMemo(() => {
    return paymentRows.reduce((sum, row) => {
      if (!allocations[row._id]?.checked) return sum;
      const amount = Number(allocations[row._id]?.amount || 0);
      return sum + (Number.isFinite(amount) ? amount : 0);
    }, 0);
  }, [allocations, paymentRows]);

  const paymentUnallocated = useMemo(() => {
    const total = Number(paymentForm.amount || 0);
    if (!Number.isFinite(total)) return 0;
    return Math.max(0, total - selectedAllocationTotal);
  }, [paymentForm.amount, selectedAllocationTotal]);

  const loadPayments = async (supplierName = '') => {
    // D49 phase 5: a VOIDED payment is history, not money paid; this list is for what counts.
    const res = await listAccountsPayablePayments({ page: 1, limit: 50, supplier: supplierName.trim(), status: 'applied' });
    const data = res.data;
    setPaymentRecords(data.items || []);
    setPaymentSummary(
      data.summary || {
        paymentCount: 0,
        totalAmount: 0,
        allocatedAmount: 0,
        unallocatedAmount: 0,
      }
    );
  };

  /**
   * Everything the payment modal shows for one supplier: their payment history,
   * their open payables, and a fresh allocation row per payable.
   *
   * ONE loader, called from both the open handler and the debounced supplier
   * effect. They used to hold byte-identical copies of this block, and both
   * replaced paymentRows/allocations unconditionally — so opening the modal and
   * typing a supplier name started two fetches, and if the earlier one answered
   * last the modal showed a different supplier's invoices under the name on the
   * form, with the allocation checkboxes keyed to those invoice ids. That is a
   * payment being allocated against the wrong supplier's payables, not a
   * cosmetic flicker. TECH_DEBT #46.
   *
   * The guard is last-one-wins rather than an abort: the two requests are
   * ordinary reads and there is nothing to cancel, only an answer to ignore.
   */
  const loadPaymentModalData = async (supplierName: string) => {
    lastLoadedFilter.current = supplierName;
    const isCurrent = beginPaymentModalRequest();
    setPaymentLoading(true);
    setPaymentLoadError(null);
    // Cleared at the START, not only on failure: while this request is in
    // flight the form must not hold the previous supplier's invoice ids.
    setPaymentRows([]);
    setAllocations({});
    try {
      const [paymentsRes, apRes] = await Promise.all([
        listAccountsPayablePayments({ page: 1, limit: 50, supplier: supplierName, status: 'applied' }),
        listAccountsPayables({
          page: 1,
          limit: 100,
          status: 'all',
          supplier: supplierName,
          q: '',
        }),
      ]);
      // A newer supplier has been asked for since; this answer is stale and
      // must not touch the rows the allocation checkboxes are keyed to.
      if (!isCurrent()) return;
      const paymentData = paymentsRes.data;
      const apData = apRes.data;
      setPaymentRecords(paymentData.items || []);
      setPaymentSummary(
        paymentData.summary || {
          paymentCount: 0,
          totalAmount: 0,
          allocatedAmount: 0,
          unallocatedAmount: 0,
        }
      );
      setPaymentRows(apData.items || []);
      const nextAllocations: Record<string, { checked: boolean; amount: string }> = {};
      (apData.items || []).forEach((row) => {
        nextAllocations[row._id] = {
          checked: row._id === preTickBillId.current,
          amount: String(Number(row.outstandingAmount || 0).toFixed(2)),
        };
      });
      setAllocations(nextAllocations);
    } catch (err: any) {
      if (!isCurrent()) return;
      // Clear, do not keep: the rows and allocation ids on screen belonged to
      // the PREVIOUS supplier, and a payment saved now would carry this
      // supplier's name over that supplier's invoices.
      setPaymentRecords([]);
      setPaymentSummary({ paymentCount: 0, totalAmount: 0, allocatedAmount: 0, unallocatedAmount: 0 });
      setPaymentRows([]);
      setAllocations({});
      setPaymentLoadError(err?.message || 'The invoices for this supplier could not be loaded.');
    } finally {
      if (isCurrent()) setPaymentLoading(false);
    }
  };

  // Takes the supplier explicitly: the caller sets the form's supplier in the
  // same tick, and reading `paymentForm` here would see the PREVIOUS value
  // (state is not updated until the next render), so the first request for
  // the modal loaded the wrong supplier's allocations.
  const openPaymentModal = async (supplierName?: string) => {
    setPaymentMode('full');
    setQuickBill(null);
    setPaymentError('');
    setHoldAsCredit(false);
    setPaymentBillFilter((supplierName ?? paymentBillFilter).trim());
    setPaymentOpen(true);
    await loadPaymentModalData((supplierName ?? paymentBillFilter).trim());
  };

  /**
   * Pay ONE bill (2026-09-22, at the owner's word): the everyday path from a row's Pay button, or
   * from "Record a payment" on the bill's own page. Five fields, prefilled where it is safe to
   * (amount = what it owes, date = today), never defaulted where D49 says not to (method).
   *
   * The full allocation table still loads quietly behind this (same `loadPaymentModalData` the full
   * mode uses), so "+ Pay another bill from this supplier" can switch to it with the rows already
   * there -- nobody waits twice.
   */
  const openQuickPay = async (bill: AccountsPayableRecord) => {
    setPaymentMode('quick');
    setQuickBill(bill);
    setPaymentError('');
    setHoldAsCredit(false);
    preTickBillId.current = bill._id;
    setPaymentForm((prev) => ({
      ...prev,
      supplierName: '',
      supplierVendorId: null,
      currency: bill.currency || 'TTD',
      amount: Number(bill.outstandingAmount || 0).toFixed(2),
      reference: '',
      chequeNumber: '',
      onlineReferenceNumber: '',
      paymentDate: browserToday(),
      method: '',
      note: '',
    }));
    setPaymentBillFilter((bill.supplierName || '').trim());
    setPaymentOpen(true);
    await loadPaymentModalData((bill.supplierName || '').trim());
  };

  const openPaymentImportModal = () => {
    setPaymentImportSummary(null);
    setPaymentImportFile(null);
    setPaymentImportOpen(true);
  };

  const closePaymentImportModal = () => {
    setPaymentImportOpen(false);
  };

  async function submitPaymentImport() {
    if (!paymentImportFile) {
      alert('Select a CSV or Excel file to import.');
      return;
    }
    setPaymentImportLoading(true);
    try {
      const res = await importAccountsPayablePayments(paymentImportFile);
      const data = res.data;
      setPaymentImportSummary({
        createdCount: Number(data.createdCount || 0),
        errors: Array.isArray(data.errors) ? data.errors : [],
      });
      await load();
      await loadPayments(supplier);
    } catch (e: any) {
      alert(e?.message || 'Failed to import payment file');
    } finally {
      setPaymentImportLoading(false);
    }
  }

  const closePaymentModal = () => {
    setPaymentOpen(false);
    setHoldAsCredit(false);
    setPaymentMode('quick');
    setQuickBill(null);
    setPaymentError('');
    preTickBillId.current = null;
  };

  // "Record a payment" on a bill's own page lands here as /accounts?tab=payable&pay=<billId> (D49 phase 5:
  // Mark Paid no longer marks anything, it sends the person to record the payment). Opens the window for
  // that bill's supplier with the bill ticked at what it still owes. The param is removed at once, so a
  // refresh or Back does not reopen the window.
  useEffect(() => {
    const payId = new URLSearchParams(location.search).get('pay');
    if (!payId) return;
    let cancelled = false;
    (async () => {
      try {
        const bill = (await getAccountsPayableById(payId)).data;
        if (cancelled) return;
        const owed = Number(bill.outstandingAmount || 0);
        if (owed > 0) {
          void openQuickPay(bill);
        } else {
          alert('This bill has nothing left to pay.');
        }
      } catch {
        if (!cancelled) alert('That bill could not be opened for payment. Find it in the list and use Record payment.');
      }
      if (!cancelled) navigate('/accounts?tab=payable', { replace: true });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    // The openers load the window themselves; a second load of the same bills would clear the ticks made since.
    if (!paymentOpen || paymentBillFilter === lastLoadedFilter.current) return;
    const t = window.setTimeout(() => {
      void loadPaymentModalData(paymentBillFilter);
    }, 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentOpen, paymentBillFilter]);

  /** Pay ONE bill (`paymentMode === 'quick'`): a single allocation, no table, checked here rather
   * than round-tripping to the server for a mistake a person can see at a glance. */
  async function submitQuickPayment() {
    if (!quickBill) return;
    const amount = Number(paymentForm.amount || 0);
    if (!(amount > 0)) return setPaymentError('Enter how much this payment is for.');
    if (!paymentForm.paymentDate) return setPaymentError('Choose the payment date.');
    if (!paymentForm.method) return setPaymentError('Choose how this was paid.');
    if (!paymentForm.reference.trim()) return setPaymentError('Enter a reference for this payment (a cheque or transfer number).');

    setPaymentError('');
    setPaymentSubmitting(true);
    try {
      const res = await createPaymentVoucher({
        supplierName: (quickBill.supplierName || '').trim() || undefined,
        reference: paymentForm.reference.trim(),
        paymentDate: paymentForm.paymentDate,
        amount,
        currency: paymentForm.currency,
        method: paymentForm.method,
        note: paymentForm.note,
        allocations: [{ accountsPayableId: quickBill._id, amount }],
      });
      const created = res.data;
      closePaymentModal();
      await load();
      await loadPayments((quickBill.supplierName || '').trim());
      if (created?._id) navigate(`/accounts/payments/${created._id}`);
    } catch (e: any) {
      // Same rule as the full window: the words are a function of the refusal's code, never the
      // server's own sentence -- shown inline here rather than an alert, since the whole point of
      // the quick window is not to interrupt with a popup for something this small.
      setPaymentError(billActionRefusalMessage(e));
    } finally {
      setPaymentSubmitting(false);
    }
  }

  async function submitPayment() {
    if (!paymentDateValid) return setPaymentError('Fix the payment date first.');
    if (paymentMode === 'quick') return submitQuickPayment();

    const selected = paymentRows
      .filter((row) => allocations[row._id]?.checked)
      .map((row) => ({
        accountsPayableId: row._id,
        amount: Number(allocations[row._id]?.amount || 0),
      }))
      .filter((item) => Number.isFinite(item.amount) && item.amount > 0);

    // Every ticked bill must carry an amount. A ticked bill at 0 used to be dropped in silence,
    // and the payee shown (the first TICKED bill) could then differ from the one paid.
    // Named, because the offending row is often scrolled out of sight in the bill table.
    if (selected.length !== tickedRows.length) {
      const sent = new Set(selected.map((item) => item.accountsPayableId));
      const missing = tickedRows.filter((row) => !sent.has(row._id)).map((row) => row.invoiceNumber || '(no number)');
      setPaymentError(`Enter an amount on every ticked invoice, or untick it: ${missing.join(', ')}.`);
      return;
    }

    // An excess over the ticked bills, or no bills at all (an advance), is held as a supplier credit
    // ONLY when the box is ticked. With no bills and no tick there is nothing to record.
    const excessAmount = Number(paymentForm.amount || 0) - selected.reduce((sum, item) => sum + item.amount, 0);
    const holdExcess = holdAsCredit && excessAmount > EXCESS_THRESHOLD;
    if (!selected.length && !holdExcess) {
      setPaymentError('Select at least one invoice to allocate the payment, or tick the box to record it as an advance.');
      return;
    }

    // D86: a vendor pick is needed only for an advance (below), the one place a vendor id is used.
    // Ticked bills name their own supplier: send the name they are stored under, which is not
    // always a vendor's exact name ("ALPHA MARINE LIMITED" vs "Alpha Marine Ltd").
    const billsSupplier = selected.length
      ? paymentRows.find((row) => row._id === selected[0].accountsPayableId)?.supplierName?.trim()
      : '';
    const sentSupplierName = selected.length ? billsSupplier || '' : paymentForm.supplierName.trim();

    setPaymentSubmitting(true);
    try {
      // An advance is held against a REGISTERED vendor: the one picked as the supplier.
      let advanceVendorId: string | undefined;
      if (!selected.length) {
        if (!paymentForm.supplierVendorId) {
          setPaymentError('Pick the supplier the advance is for. An advance is held against a registered vendor.');
          return;
        }
        advanceVendorId = paymentForm.supplierVendorId;
      }
      const res = await createPaymentVoucher({
        supplierName: sentSupplierName || undefined,
        reference: paymentForm.reference.trim(),
        chequeNumber: paymentForm.chequeNumber.trim(),
        onlineReferenceNumber: paymentForm.onlineReferenceNumber.trim(),
        paymentDate: paymentForm.paymentDate,
        amount: Number(paymentForm.amount || 0),
        currency: paymentForm.currency,
        method: paymentForm.method,
        note: paymentForm.note,
        allocations: selected,
        ...(holdExcess ? { holdRemainderAsCredit: true } : {}),
        ...(advanceVendorId ? { vendorId: advanceVendorId } : {}),
      });
      const created = res.data;
      setPaymentOpen(false);
      setHoldAsCredit(false);
      preTickBillId.current = null;
      await load();
      await loadPayments(sentSupplierName);
      if (created?._id) {
        navigate(`/accounts/payments/${created._id}`);
      }
    } catch (e: any) {
      // The words are a function of the refusal's code (or a 403); the server's own sentence is not shown.
      setPaymentError(billActionRefusalMessage(e));
    } finally {
      setPaymentSubmitting(false);
    }
  }

  // One place the refusal is drawn, beside the Save/Pay button of whichever window is open.
  const paymentErrorNotice = paymentError ? (
    <div role="alert" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
      {paymentError}
    </div>
  ) : null;

  // formatCurrency is the labelled-money owner: no code means a bare number, never a guessed one (D96.14); null, not undefined (an undefined argument takes the 'USD' default).
  const formatMoney2 = (v: number | undefined, cur?: string | null) =>
    typeof v !== 'number' || Number.isNaN(v) ? '--' : formatCurrency(v, cur || null, { ceil: false });

  // One figure per currency present, never one figure claiming to be all of
  // them (t09). The common case (every invoice in one currency) still reads
  // as a single amount.
  const totalsEntries = Object.entries(clientInvoiceSummary?.totalsByCurrency ?? {});
  const totalValueDisplay = totalsEntries.length
    ? totalsEntries
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([cur, amt]) => formatMoney2(amt, cur))
        .join(' + ')
    : '--';

  return (
    <div className="h-full flex flex-col bg-brand-bg">
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold leading-7 text-slate-900">Accounts</h1>
            <ModuleInfoButton content={INFO} ariaLabel="Accounts payable information" />
          </div>
        </div>

        {/* Tab switcher. The two sub-labels ("What you owe vendors" / "What
            clients owe you") moved into the tooltip the module info button
            already carries: a two-line tab is the only two-line control in the
            app, and AccountsPage.test.tsx finds these by their main label. */}
        <SegmentedControl
          aria-label="Accounts view"
          value={activeTab}
          onChange={(id) => handleTabChange(id as typeof activeTab)}
          segments={[
            { value: 'payable', label: 'Accounts Payable' },
            { value: 'receivable', label: 'Client Invoices' },
            { value: 'margin', label: 'Margin by vessel' },
          ]}
        />
      </div>

      {/* ── Margin by vessel (D119.13) ───────────────────────────── */}
      {activeTab === 'margin' && <MarginByVesselPanel onOpenInvoice={(id) => navigate(`/client-invoices/${id}`)} />}

      {/* ── Client Invoices Tab ─────────────────────────────────── */}
      {activeTab === 'receivable' && (
        <>
        {/* The same strip as Accounts Payable, in the same place: under the tab switcher, search and Export CSV at the right (UI-05). */}
        <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-2 sm:px-8">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <SearchInput
              value={clientInvoicesSearch}
              onChange={setClientInvoicesSearch}
              placeholder="Search here"
              className="w-64"
            />
            <Button
              variant="primary"
              onClick={() =>
                downloadObjectsCsv(
                  'client-invoices.csv',
                  clientInvoices.map((inv) => ({
                    'Invoice #': inv.invoiceNumber || '',
                    Client: inv.clientName || '',
                    Vessel: inv.vesselName || '',
                    Quotation: inv.quotationNumber || '',
                    Status: String(inv.status || 'draft'),
                    Currency: inv.currency || '',
                    Total: inv.summary?.invoiceGrandTotal ?? inv.summary?.quotedTotal ?? '',
                    Created: fmtDate(inv.createdAt, ''),
                  }))
                )
              }
              disabled={clientInvoices.length === 0}
              icon={<Download aria-hidden="true" className="h-4 w-4" />}
            >
              Export CSV
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-auto px-8 py-4 space-y-4">
          {clientInvoiceSummaryFailed ? (
            <p role="alert" className="text-sm text-rose-700">The invoice totals could not be loaded. Check your connection and try again.</p>
          ) : null}
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Total Invoices', value: clientInvoiceSummary ? clientInvoiceSummary.counts.all : '--', color: 'text-slate-800' },
              { label: 'Total Value', value: totalValueDisplay, color: 'text-cyan-700' },
              { label: 'Sent', value: clientInvoiceSummary ? clientInvoiceSummary.counts.sent ?? 0 : '--', color: 'text-sky-700' },
              { label: 'Draft', value: clientInvoiceSummary ? clientInvoiceSummary.counts.draft ?? 0 : '--', color: 'text-amber-600' },
            ].map(({ label, value, color }) => (
              <div key={label} className="rounded-xl bg-white border border-slate-200 p-4">
                <div className="text-xs text-slate-400 uppercase tracking-wide">{label}</div>
                <div className={`text-xl font-bold mt-1 ${color}`}>{value}</div>
              </div>
            ))}
          </div>

          <span className="block text-xs tabular-nums text-slate-500">{clientInvoicesTotal} invoice{clientInvoicesTotal !== 1 ? 's' : ''}</span>

          {/* Table */}
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            {clientInvoicesLoading ? (
              <LoadingState text="Loading invoices..." fullHeight={false} className="py-12" size={72} />
            ) : (
              <div className="overflow-auto">
                <table className="data-table data-table-dense">
                  <thead className="sticky top-0 z-10">
                    <tr>
                      <th>Invoice #</th>
                      <th>Client</th>
                      <th>Vessel</th>
                      <th>Quotation</th>
                      <th>Status</th>
                      <th>Accounting</th>
                      <th>Discrepancies</th>
                      <th>Delivery slips</th>
                      <th className="text-right">Total</th>
                      <th>Created</th>
                      <th className="w-8">
                        <span className="sr-only">Open</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientInvoices.length === 0 && (
                      <tr>
                        <td colSpan={11} className="px-4 py-12 text-center text-slate-400">
                          No client invoices found.
                        </td>
                      </tr>
                    )}
                    {clientInvoices.map((inv) => {
                      const total = inv.summary?.invoiceGrandTotal ?? inv.summary?.quotedTotal ?? 0;
                      return (
                        <tr
                          key={inv._id}
                          onClick={() => navigate(`/client-invoices/${inv._id}`)}
                          className="cursor-pointer group"
                        >
                          <td className="font-medium text-slate-900">{inv.invoiceNumber || '--'}</td>
                          <td>{inv.clientName || '--'}</td>
                          <td className="text-slate-500">{inv.vesselName || '--'}</td>
                          <td className="text-slate-500">{inv.quotationNumber || '--'}</td>
                          <td>
                            {/* One badge, one colour map: StatusBadge owns "Sent" and
                                "Draft" everywhere. This cell had its own third map. */}
                            <StatusBadge status={String(inv.status || 'draft')} />
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <select
                              value={(inv as any).accountingStatus || 'not_sent'}
                              onChange={async (e) => {
                                e.stopPropagation();
                                const newStatus = e.target.value; // capture before await
                                setClientInvoices(prev => prev.map(r => r._id === inv._id ? { ...r, accountingStatus: newStatus } as any : r));
                                try {
                                  await patchClientInvoice(inv._id, { accountingStatus: newStatus } as any);
                                } catch {
                                  // revert on failure
                                  setClientInvoices(prev => prev.map(r => r._id === inv._id ? { ...r, accountingStatus: (inv as any).accountingStatus || 'not_sent' } as any : r));
                                }
                              }}
                              aria-label="Accounting status"
                              // D96.1: a cancelled invoice is final - the server refuses any change to it.
                              disabled={inv.status === 'cancelled'}
                              title={inv.status === 'cancelled' ? 'This invoice is cancelled' : undefined}
                              className={`disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer rounded-full border px-2 py-1 text-xs font-medium outline-none ${accountingStatusClass((inv as any).accountingStatus)}`}
                            >
                              <option value="not_sent">Not sent</option>
                              <option value="accounting_team">Accounting team</option>
                            </select>
                          </td>
                          <td>
                            {(inv.summary as any)?.discrepancyCount > 0 ? (
                              <span className="font-semibold tabular-nums text-amber-700">{(inv.summary as any).discrepancyCount}</span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="tabular-nums">{(inv.summary as any)?.deliverySlipCount ?? 0}</td>
                          <td className="text-right font-semibold tabular-nums text-slate-900">
                            {total > 0 ? formatMoney2(total, inv.currency) : '--'}
                          </td>
                          <td className="whitespace-nowrap text-slate-500">
                            {fmtDate(inv.createdAt, '--')}
                          </td>
                          <td className="text-slate-400 transition-colors group-hover:text-primary-500">
                            <ChevronRight aria-hidden="true" className="h-4 w-4" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        </>
      )}

      {/* ── Accounts Payable Tab ────────────────────────────────── */}
      {activeTab === 'payable' && (
      <div className="h-full flex flex-col bg-brand-bg">
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-2 sm:px-8">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <SearchInput
            value={search}
            onChange={(next) => changeFilters({ q: next })}
            placeholder="Search here"
            className="w-64"
          />

          <input
            value={supplier}
            // Typing is a search: it drops the exact match a report link may have set.
            onChange={(e) => changeFilters({ supplier: e.target.value, supplierExact: false })}
            placeholder="Filter by supplier"
            aria-label="Filter by supplier"
            className="h-9 w-56 rounded-md border border-slate-200 bg-white px-3 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          />

          <Button onClick={() => syncAndLoad(true)} loading={syncing} icon={<RefreshCw aria-hidden="true" className="h-4 w-4" />}>
            {syncing ? 'Syncing…' : 'Sync'}
          </Button>

          {canRecordPayments ? (
            <Button
              onClick={() => {
                setPaymentForm((prev) => ({ ...prev, supplierName: '', supplierVendorId: null }));
                void openPaymentModal(supplier);
              }}
              title="Pay several bills for one supplier in one payment"
            >
              Record payment
            </Button>
          ) : null}

          <Button onClick={() => navigate('/accounts/vouchers')}>
            Payment vouchers
          </Button>

          <Button onClick={() => navigate('/accounts/payment-runs')}>
            Payment runs
          </Button>

          <Button onClick={() => navigate('/accounts/reports')}>
            Reports
          </Button>

          <Button
            variant="primary"
            onClick={() => downloadObjectsCsv('accounts-payable.csv', exportRows)}
            icon={<Download aria-hidden="true" className="h-4 w-4" />}
          >
            Export CSV
          </Button>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Vendor invoice payables are synced from the existing Vendor Invoices module.
        </p>
      </div>

      <div className="flex-1 overflow-auto px-4 py-4 sm:px-8">
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
            <SummaryCard label="Invoices" value={summary.invoiceCount} />
          <SummaryCard label="Unpaid" value={summary.unpaidCount} />
          <SummaryCard label="Partial" value={summary.partialCount} />
          <SummaryCard label="Paid" value={summary.paidCount} />
          <SummaryCard label="Outstanding" value={<CurrencyLines byCurrency={summary.byCurrency} field="outstandingAmount" />} />
          <SummaryCard label="Overdue" value={summary.overdueCount} />
          <SummaryCard label="Paid Amount" value={<CurrencyLines byCurrency={summary.byCurrency} field="paidAmount" />} />
        </div>

        <div className="mb-4">
          <SegmentedControl
            aria-label="Payment status"
            value={status}
            onChange={(next) => changeFilters({ status: next as typeof status })}
            segments={STATUS_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
          />
        </div>

        <PayableFilterBar
          filters={filters}
          onChange={changeFilters}
          onClear={() => {
            setPage(1);
            clearFilters();
          }}
        />

        {loadError ? (
          <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
            {loadError}
          </div>
        ) : null}

        {syncNote ? (
          <div role="status" className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {syncNote}
          </div>
        ) : null}

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          {/* `relative`: the "Pay" header's sr-only span is absolutely positioned, and without a
              positioned ancestor it escapes this scroll region and widens the whole document
              (the same bug the Reports pages hit, 2026-09-22 QA -- fixed there by this same class). */}
          <div className="relative overflow-auto">
            <table className="data-table min-w-[1120px]">
              <thead>
                <tr>
                  <th className="w-[110px]">Invoice number</th>
                  <th>Supplier name</th>
                  <th className="text-right">Total amount</th>
                  <th className="text-right">Paid amount</th>
                  <th className="text-right">Outstanding</th>
                  <th>Payment due date</th>
                  <th>Status</th>
                  <th>Sent to accountant?</th>
                  <th className="w-[90px]">
                    <span className="sr-only">Pay</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {!loading && !loadError && items.length === 0 && (
                  <tr>
                    {/* Copy pinned by AccountsPage.test.tsx. */}
                    <td colSpan={9} className="py-10 text-center text-slate-500">
                      No accounts payable records found.
                    </td>
                  </tr>
                )}
                {items.map((row) => (
                  <tr key={row._id} className={`border-b border-slate-100 hover:bg-slate-50 ${rowToneClass(row)}`}>
                    <td className="px-4 py-3 text-slate-800 font-medium whitespace-nowrap w-[110px]">
                      <button
                        onClick={() => navigate(`/accounts/${row._id}`)}
                        className="text-left text-cyan-700 hover:underline"
                        title="Open invoice detail"
                      >
                        {row.invoiceNumber || '--'}
                      </button>
                      {/* Shown because the list can now be filtered by it. */}
                      <div className="text-xs font-normal text-slate-500">{formatDate(row.invoiceDate)}</div>
                    </td>
                    <td className="min-w-[220px]">
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() =>
                            navigate(`/accounts/suppliers/${encodeURIComponent(row.supplierName || 'Unknown')}`)
                          }
                          className="text-left font-medium text-primary-500 hover:underline"
                          title="Open supplier details"
                        >
                          {row.supplierName || '--'}
                        </button>
                        {vendorTermsLabel(row.vendorCreditDays) ? (
                          <span
                            className={`inline-flex w-fit items-center rounded-full border px-2 py-1 text-xs font-medium ${vendorTermsClass(
                              row.vendorCreditDays
                            )}`}
                          >
                            {vendorTermsLabel(row.vendorCreditDays)}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="whitespace-nowrap text-right tabular-nums">
                      {formatMoney(row.totalAmount, row.currency || 'TTD')}
                    </td>
                    <td
                      className="whitespace-nowrap text-right tabular-nums"
                      title="Paid amounts come from recorded payments. Use Record payment."
                    >
                      {/* D49 phase 5: read-only. Every cent paid has a voucher, a date and a method, so the
                          supplier statement always adds up. It used to be typed straight into the list. */}
                      {formatMoney(row.paidAmount ?? 0, row.currency || 'TTD')}
                      {/* Outstanding is total - paid - applied credits: a credited bill says what was
                          credited, so the three figures add up on the row (lane 5, V3a). The server
                          works it out (`creditedAmount`); nothing is added up here. */}
                      {(row.creditedAmount ?? 0) > 0 ? (
                        <div className="text-xs font-normal text-purple-700" title="Taken off by a vendor return or a vendor credit. Outstanding = Total - Paid - Credit.">
                          Credit −{formatMoney(row.creditedAmount, row.currency || 'TTD')}
                        </div>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap text-right font-medium tabular-nums text-slate-900">
                      {formatMoney(row.outstandingAmount, row.currency || 'TTD')}
                    </td>
                    <td className="min-w-[150px]">
                      <div className="space-y-1">
                        <EditableCell
                          value={toDateInputValue(row.dueDate)}
                          type="date"
                          onSave={(value) => saveField(row._id, { dueDate: value })}
                        />
                        {/* D34, flag never block: a due date before the invoice date is a misread of one
                            of the two. The same check the review screen runs, so the bills already stored
                            that way (lane 5, V3b) can be found and put right here by editing the date. */}
                        {invoiceDateChecks({ invoiceDate: row.invoiceDate, dueDate: row.dueDate }).dueBeforeInvoice ? (
                          <div
                            className="text-xs font-semibold text-amber-700"
                            title="This bill falls due before the date on its invoice, so one of the two dates is wrong. Check both against the invoice, then correct the due date here."
                          >
                            Due before the invoice date
                          </div>
                        ) : null}
                        {vendorTermsLabel(row.vendorCreditDays) ? (
                          <div className="text-xs font-semibold text-slate-500">
                            {vendorTermsLabel(row.vendorCreditDays)}
                          </div>
                        ) : null}
                        {dueDateBasisCaption(row) ? (
                          <div
                            className="text-xs italic text-slate-400"
                            title="The days between the invoice date and this due date, and where they came from. Edit the cell to override."
                          >
                            {dueDateBasisCaption(row)}
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className="min-w-[140px]">
                      {/* The plain word the everyday list reads (2026-09-22): Paid, Unpaid or
                          Partially Paid, read off the money -- never editable here. A person
                          changes a bill to On Hold or Disputed from the bill's own page, where the
                          reason it needs one is right there; the list only flags that it happened. */}
                      <span
                        data-ap-status={row.status}
                        title={String(row.status || '').toLowerCase() === 'overdue' ? 'Overdue' : undefined}
                        className={`inline-flex w-fit items-center rounded-full border px-3 py-1 text-xs font-medium ${simpleStatusBadgeClass(
                          row
                        )}`}
                      >
                        {simpleBillStatusLabel(row)}
                        {String(row.status || '').toLowerCase() === 'overdue' ? ' · Overdue' : ''}
                      </span>
                      {isExceptionStatus(row.status) ? (
                        <div
                          className="mt-1 text-xs font-semibold text-amber-700"
                          title="Open the bill to see why, and to change it"
                        >
                          {'⚠'} {exceptionStatusLabel(row.status)}
                        </div>
                      ) : null}
                    </td>
                    <td className="min-w-[150px]">
                      <select
                        aria-label="Sent to accountant?"
                        value={row.accountingStatus || 'not_sent'}
                        onChange={(e) => saveField(row._id, { accountingStatus: e.target.value })}
                        className={`w-full rounded-md border px-2 py-1 text-xs outline-none ${accountingStatusClass(
                          row.accountingStatus
                        )}`}
                      >
                        {ACCOUNTING_STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="min-w-[90px]">
                      {!canRecordPayments || Number(row.outstandingAmount || 0) <= 0.005 ? (
                        <span className="text-xs text-slate-400">--</span>
                      ) : isExceptionStatus(row.status) ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/accounts/${row._id}`)}
                          className="text-xs font-medium text-amber-700 hover:underline"
                          title={`${exceptionStatusLabel(row.status)} -- open the bill first`}
                        >
                          Review
                        </button>
                      ) : (
                        <button
                          type="button"
                          aria-label={`Pay bill ${row.invoiceNumber || row._id}`}
                          onClick={() => void openQuickPay(row)}
                          className="inline-flex h-8 items-center rounded-md border border-cyan-200 bg-cyan-50 px-3 text-xs font-semibold text-cyan-700 hover:bg-cyan-100"
                        >
                          Pay
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination page={page} pageSize={AP_PAGE_SIZE} total={summary.invoiceCount} onPageChange={setPage} noun="invoices" />
        </div>

      </div>

      <Modal
        isOpen={paymentOpen}
        onClose={closePaymentModal}
        title={paymentMode === 'quick' && quickBill ? `Pay invoice ${quickBill.invoiceNumber || quickBill._id}` : 'Record Payment'}
        maxWidthClass={paymentMode === 'quick' && quickBill ? 'max-w-lg' : 'max-w-6xl'}
      >
        {paymentMode === 'quick' && quickBill ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              {quickBill.supplierName || 'This supplier'} owes{' '}
              <span className="font-semibold text-slate-900">
                {formatMoney(quickBill.outstandingAmount, quickBill.currency || 'TTD')}
              </span>{' '}
              on this invoice.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                {/* The helper line below the field must sit OUTSIDE the <label> -- an implicit
                    label's accessible name is its WHOLE text content, so a hint sentence inside it
                    made `getByLabelText('Amount')` stop matching just as the "*" marker once did. */}
                <label className="block">
                  <div className={`text-xs font-medium text-slate-600 mb-1 ${REQUIRED_LABEL}`}>Amount</div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm((prev) => ({ ...prev, amount: e.target.value }))}
                    className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-cyan-300"
                    placeholder="0.00"
                  />
                </label>
                <div className="mt-1 text-xs text-slate-400">Less than the full amount is a part payment.</div>
              </div>
              <div className="block">
                {/* The box is NOT inside the label: its inline error would become part of the input's accessible name. */}
                <label htmlFor="accounts-payment-date-1" className={`block text-xs font-medium text-slate-600 mb-1 ${REQUIRED_LABEL}`}>Payment Date</label>
                <DateInput
                  id="accounts-payment-date-1"
                  value={paymentForm.paymentDate}
                  max={browserLatestPaymentDay()}
                  onChange={(v) => setPaymentForm((prev) => ({ ...prev, paymentDate: v }))}
                  validity={setPaymentDateValid}
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-cyan-300"
                />
              </div>
              <label className="block">
                <div className={`text-xs font-medium text-slate-600 mb-1 ${REQUIRED_LABEL}`}>Method</div>
                <select
                  value={paymentForm.method}
                  onChange={(e) => setPaymentForm((prev) => ({ ...prev, method: e.target.value }))}
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-cyan-300"
                >
                  <option value="" disabled>
                    Choose how this was paid
                  </option>
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <div className={`text-xs font-medium text-slate-600 mb-1 ${REQUIRED_LABEL}`}>Voucher Reference</div>
                <input
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm((prev) => ({ ...prev, reference: e.target.value }))}
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-cyan-300"
                  placeholder="Cheque or transfer number"
                />
              </label>
            </div>

            <label className="block">
              <div className={`text-xs font-medium text-slate-600 mb-1 ${OPTIONAL_LABEL}`}>Note</div>
              <input
                value={paymentForm.note}
                onChange={(e) => setPaymentForm((prev) => ({ ...prev, note: e.target.value }))}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-cyan-300"
                placeholder="Optional note"
              />
            </label>

            <button
              type="button"
              onClick={() => {
                setAllocations((prev) => ({
                  ...prev,
                  [quickBill._id]: { checked: true, amount: paymentForm.amount || '0' },
                }));
                setPaymentMode('full');
              }}
              className="text-xs font-medium text-cyan-700 hover:underline"
            >
              + Pay another bill from {quickBill.supplierName || 'this supplier'} in the same payment
            </button>

            {paymentErrorNotice}

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={closePaymentModal}
                className="px-4 py-2 rounded-md border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void submitPayment()}
                disabled={paymentSubmitting}
                className="px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 disabled:opacity-50"
              >
                {paymentSubmitting ? 'Paying...' : 'Pay'}
              </button>
            </div>
          </div>
        ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="block">
              <div className={`text-xs font-medium text-slate-600 mb-1 ${OPTIONAL_LABEL}`}>Find bills</div>
              <input
                value={paymentBillFilter}
                onChange={(e) => setPaymentBillFilter(e.target.value)}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-cyan-300"
                placeholder="Search bills by supplier"
              />
            </label>
            {hasTickedBills ? (
              <div className="block" data-testid="payment-payee">
                <div className="text-xs font-medium text-slate-600 mb-1">Paying</div>
                <div className="flex h-10 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-800">
                  {tickedSupplierName || '--'}
                </div>
              </div>
            ) : (
              <VendorSelect
                label="Advance to vendor"
                value={{ vendorId: paymentForm.supplierVendorId, name: paymentForm.supplierName }}
                onChange={(v) => setPaymentForm((prev) => ({ ...prev, supplierName: v.name, supplierVendorId: v.vendorId }))}
                placeholder="Only for an advance"
              />
            )}
            <label className="block">
              <div className={`text-xs font-medium text-slate-600 mb-1 ${REQUIRED_LABEL}`}>Voucher Reference</div>
              <input
                value={paymentForm.reference}
                onChange={(e) => setPaymentForm((prev) => ({ ...prev, reference: e.target.value }))}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-cyan-300"
                placeholder="Payment voucher ref"
              />
            </label>
            <label className="block">
              <div className={`text-xs font-medium text-slate-600 mb-1 ${OPTIONAL_LABEL}`}>Cheque Number</div>
              <input
                value={paymentForm.chequeNumber}
                onChange={(e) => setPaymentForm((prev) => ({ ...prev, chequeNumber: e.target.value }))}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-cyan-300"
                placeholder="Cheque number"
              />
            </label>
            <label className="block">
              <div className={`text-xs font-medium text-slate-600 mb-1 ${OPTIONAL_LABEL}`}>Online Reference Number</div>
              <input
                value={paymentForm.onlineReferenceNumber}
                onChange={(e) => setPaymentForm((prev) => ({ ...prev, onlineReferenceNumber: e.target.value }))}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-cyan-300"
                placeholder="Online / bank reference"
              />
            </label>
            <div className="block">
              {/* The box is NOT inside the label: its inline error would become part of the input's accessible name. */}
              <label htmlFor="accounts-payment-date-2" className={`block text-xs font-medium text-slate-600 mb-1 ${REQUIRED_LABEL}`}>Payment Date</label>
              <DateInput
                id="accounts-payment-date-2"
                value={paymentForm.paymentDate}
                max={browserLatestPaymentDay()}
                onChange={(v) => setPaymentForm((prev) => ({ ...prev, paymentDate: v }))}
                validity={setPaymentDateValid}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-cyan-300"
              />
            </div>
            <label className="block">
              <div className={`text-xs font-medium text-slate-600 mb-1 ${REQUIRED_LABEL}`}>Amount</div>
              <input
                type="number"
                step="0.01"
                min="0"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm((prev) => ({ ...prev, amount: e.target.value }))}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-cyan-300"
                placeholder="0.00"
              />
            </label>
            <label className="block">
              <div className="text-xs font-medium text-slate-600 mb-1">Currency</div>
              <select
                value={paymentForm.currency}
                onChange={(e) => setPaymentForm((prev) => ({ ...prev, currency: e.target.value }))}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-cyan-300"
              >
                <option value="TTD">TTD</option>
                <option value="USD">USD</option>
              </select>
            </label>
            <label className="block">
              <div className={`text-xs font-medium text-slate-600 mb-1 ${REQUIRED_LABEL}`}>Method</div>
              <select
                value={paymentForm.method}
                onChange={(e) => setPaymentForm((prev) => ({ ...prev, method: e.target.value }))}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-cyan-300"
              >
                <option value="Cheque">Cheque</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cash">Cash</option>
                <option value="Card">Card</option>
                <option value="Other">Other</option>
              </select>
            </label>
          </div>

          <label className="block">
            <div className={`text-xs font-medium text-slate-600 mb-1 ${OPTIONAL_LABEL}`}>Note</div>
            <textarea
              value={paymentForm.note}
              onChange={(e) => setPaymentForm((prev) => ({ ...prev, note: e.target.value }))}
              className="w-full min-h-[80px] rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-cyan-300"
              placeholder="Optional note"
            />
          </label>

          <PaymentCreditOption
            excess={paymentUnallocated}
            currency={paymentForm.currency || 'TTD'}
            supplierName={hasTickedBills ? tickedSupplierName : paymentForm.supplierName}
            billsSelected={selectedAllocationTotal > 0}
            checked={holdAsCredit}
            onChange={setHoldAsCredit}
            disabled={paymentSubmitting}
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-1 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm font-semibold text-slate-800 mb-2">Payment Summary</div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Selected invoices</span>
                  <span className="font-semibold text-slate-800">{selectedAllocationTotal > 0 ? paymentRows.filter((row) => allocations[row._id]?.checked).length : 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Allocated total</span>
                  <span className="font-semibold text-slate-800">
                    {formatMoney(selectedAllocationTotal, paymentForm.currency || 'TTD')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Payment amount</span>
                  <span className="font-semibold text-slate-800">
                    {formatMoney(Number(paymentForm.amount || 0), paymentForm.currency || 'TTD')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Unallocated</span>
                  <span className="font-semibold text-slate-800">
                    {formatMoney(paymentUnallocated, paymentForm.currency || 'TTD')}
                  </span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 rounded-lg border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 bg-white flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-800">Invoice Allocations</div>
                  <div className="text-xs text-slate-500">
                    Select the AP invoices this payment should cover.
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const next: Record<string, { checked: boolean; amount: string }> = {};
                      // Only bills that still owe something: a paid bill (listed under 'all')
                      // ticked at 0.00 would be refused by the every-ticked-bill-has-an-amount check.
                      paymentRows.forEach((row) => {
                        const owed = Number(row.outstandingAmount || 0);
                        next[row._id] = { checked: owed > 0, amount: owed > 0 ? owed.toFixed(2) : '0' };
                      });
                      setAllocations(next);
                    }}
                    className="px-3 py-2 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const next: Record<string, { checked: boolean; amount: string }> = {};
                      paymentRows.forEach((row) => {
                        next[row._id] = { checked: false, amount: '0' };
                      });
                      setAllocations(next);
                    }}
                    className="px-3 py-2 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="max-h-[420px] overflow-auto bg-white">
                {paymentLoading ? (
                  <LoadingState text="Loading invoices..." fullHeight={false} className="py-8" size={64} />
                ) : paymentLoadError ? (
                  <div className="p-6 text-sm text-rose-700" role="alert">
                    {paymentLoadError} Nothing can be allocated until they load — close and reopen the form to retry.
                  </div>
                ) : paymentRows.length === 0 ? (
                  <div className="p-6 text-sm text-slate-500">No AP invoices found for this supplier.</div>
                ) : (
                  <table className="data-table min-w-[980px]">
                    <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                      <tr className="text-left text-xs text-slate-600">
                        <th className="px-4 py-3">Use</th>
                        <th className="px-4 py-3">Invoice #</th>
                        <th className="px-4 py-3">Outstanding</th>
                        <th className="px-4 py-3">Allocate</th>
                        <th className="px-4 py-3">Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentRows.map((row) => {
                        const allocation = allocations[row._id] || { checked: false, amount: '0' };
                        return (
                          <tr key={row._id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                aria-label={`Pay invoice ${row.invoiceNumber || row._id}`}
                                checked={allocation.checked}
                                onChange={(e) =>
                                  setAllocations((prev) => ({
                                    ...prev,
                                    [row._id]: {
                                      checked: e.target.checked,
                                      amount: e.target.checked
                                        ? String(Number(row.outstandingAmount || 0).toFixed(2))
                                        : '0',
                                    },
                                  }))
                                }
                                className="h-4 w-4 accent-cyan-600"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-800">{row.invoiceNumber || '--'}</div>
                              <div className="text-xs text-slate-500">{row.supplierName || '--'}</div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {formatMoney(row.outstandingAmount, row.currency || paymentForm.currency || 'TTD')}
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                aria-label={`Amount to pay against invoice ${row.invoiceNumber || row._id}`}
                                value={allocation.amount}
                                onChange={(e) =>
                                  setAllocations((prev) => ({
                                    ...prev,
                                    [row._id]: {
                                      checked: true,
                                      amount: e.target.value,
                                    },
                                  }))
                                }
                                disabled={!allocation.checked}
                                className="w-40 h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-cyan-300 disabled:bg-slate-50 disabled:text-slate-400"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="text"
                                aria-label={`Note on invoice ${row.invoiceNumber || row._id}`}
                                value={row.notes || ''}
                                readOnly
                                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-slate-50 text-slate-500"
                                placeholder="Invoice note"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {paymentErrorNotice}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={closePaymentModal}
              className="px-4 py-2 rounded-md border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void submitPayment()}
              disabled={paymentSubmitting}
              className="px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 disabled:opacity-50"
            >
              {paymentSubmitting ? 'Saving...' : 'Save Payment'}
            </button>
          </div>
        </div>
        )}
      </Modal>

      <Modal
        isOpen={paymentImportOpen}
        onClose={closePaymentImportModal}
        title="Import Payment File"
        maxWidthClass="max-w-3xl"
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
            Upload a CSV or Excel file with payment rows. Supported columns:
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-slate-500">
              <div>supplierName / supplier / vendor</div>
              <div>reference / chequeNo / paymentRef</div>
              <div>paymentDate / date</div>
              <div>amount</div>
              <div>invoiceNumber / invoiceNo / billNo</div>
              <div>allocationAmount / allocatedAmount</div>
              <div>currency, method, note</div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-xs font-medium text-slate-600">Payment file</div>
            <input
              type="file"
              accept=".csv,.xls,.xlsx"
              onChange={(e) => setPaymentImportFile(e.target.files?.[0] || null)}
              className="block w-full text-sm"
            />
            {paymentImportFile ? (
              <div className="text-xs text-slate-500">
                Selected: <span className="font-semibold text-slate-700">{paymentImportFile.name}</span>
              </div>
            ) : null}
          </div>

          {paymentImportSummary ? (
            <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-4 text-sm">
              <div className="font-semibold text-cyan-800">
                Imported {paymentImportSummary.createdCount} payment record(s)
              </div>
              {paymentImportSummary.errors.length > 0 ? (
                <div className="mt-2 text-rose-700">
                  <div className="font-semibold">Warnings</div>
                  <ul className="list-disc pl-5 text-xs">
                    {paymentImportSummary.errors.slice(0, 8).map((err, idx) => (
                      <li key={`${idx}-${err}`}>{err}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={closePaymentImportModal}
              className="px-4 py-2 rounded-md border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void submitPaymentImport()}
              disabled={paymentImportLoading}
              className="px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 disabled:opacity-50"
            >
              {paymentImportLoading ? 'Importing...' : 'Import'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-slate-900">{value}</div>
    </div>
  );
}
